import React, { createRef, useEffect, useState } from 'react';

import { makeStyles } from '@material-ui/core/styles';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import Paper from '@material-ui/core/Paper';
import Snackbar from '@material-ui/core/Snackbar';
import Typography from '@material-ui/core/Typography';
import MenuItem from '@material-ui/core/MenuItem';
import TextField from '@material-ui/core/TextField';
import ConfirmDialog from '../ConfirmDialog';

import LeadsImportTable from './LeadsImportTable';
import LeadsImportSMS from './LeadsImportSMS';

import { CsvBuilder } from 'filefy';


const useStyles = makeStyles(theme => ({
    paper: {
        padding: theme.spacing(3),
    },
    input: {
        display: 'none'
    },
    province: {
        width: 350
    }
}));

const LeadsList = () => {
    const classes = useStyles();
    const ref = createRef();
    const provinces = window.provinces;

    useEffect(() => {
        document.title = 'Import Leads - Enduring Promises';
    });

    const [loading, setLoading] = useState(false);
    const [fileInfo, setFileInfo] = useState('');
    const [fileUpload, setFileUpload] = useState(null);
    const [province, setProvince] = useState(46);
    const [leads, setLeads] = useState([]);

    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const handleCloseSnackbar = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setOpenSnackbar(false);
    };

    const handleBack = () => {
        window.history.back();
    };

    const handleOnClick = () => {
        ref.current.click();
    }

    const handleFileClick = (e) => {
        e.target.value = null;
        handleClear();
    }

    const handleClear = () => {
        setFileUpload(null);
        setFileInfo('');
    }

    const downloadTemplate = () => {
        const columns = [
            'Bride First Name', 'Bride Last Name', 'Groom First Name', 'Groom Last Name', 'Bride Email', 'Groom Email', 'Bride Cell', 'Wedding Venue', 'Wedding Date'
        ];
        const csvBuilder = new CsvBuilder('Import_Template.csv')
            .setColumns(columns)
            .exportFile();
    }

    const handleChangeProvince = (event) => {
        setProvince(event.target.value);
    }

    const handleUpload = (event) => {
        if (!event.target.value){
            return;
        }
        const fileSize = event.target.files[0].size / 1024 / 1024;
        if (fileSize > 5.0) {
            alert(`Please try a file smaller than 5.0 MB. Actual size: ${fileSize.toFixed(2)} MB`);
            return;
        }
        setLoading(true);
        const formData = new FormData();
        formData.append('_token', window._token);
        formData.append('csv', event.target.files[0]);
        setFileUpload(event.target.files[0]);
        let url = `/admin/leads/import/check-file`;
        fetch(url, {
            method: 'POST',
            body: formData
        })
        .then(response => {
            return response.json();
        })
        .then(result => {
            if (result.error) {
                alert(result.error);
            }
            if (result.fileInfo) {
                setFileInfo(result.fileInfo);
            }
            setLoading(false);
        })
        .catch(reason => {
            console.log(reason);
            setLoading(false);
            alert('An error prevented loading the CSV. Please try a different file (CSV format only)');
        })
    }

    const handleSubmit = () => {
        if (!province || !fileUpload) {
            alert('Missing one of the following: province, file upload');
            return;
        }
        setLoading(true);
        const formData = new FormData();
        formData.append('_token', window._token);
        formData.append('csv', fileUpload);
        formData.append('province', province);
        formData.append('filename', fileUpload.name);
        let url = '/admin/leads/import/submit';
        fetch(url, {
            method: 'POST',
            body: formData
        })
        .then(response => {
            return response.json();
        })
        .then(result => {
            if (result.error) {
                alert(result.error);
            } else {
                setLeads(result.leads);
                setSnackbarMessage('Successfully imported couples');
                setOpenSnackbar(true);
                sendInquiryDetails(result.leads);
            }
            setLoading(false);
        })
        .catch(reason => {
            setLoading(false);
            alert('An error prevented submitting. Please try again.');
        })
    };

    const sendInquiryDetails = (leads) => {
        const leadIds = leads.map(x => x.id);
        const formData = new FormData();
        formData.append('_token', window._token);
        formData.append('leads', leadIds);
        let url = '/admin/leads/import/send-inquiry-details';
        fetch(url, {
            method: 'POST',
            body: formData
        })
        .then(response => {
            return response.json();
        })
        .then(result => {
            console.log('Sent inquiry details');
        })
        .catch(reason => {
            alert('An error prevented sending inquiry details');
        })
    }

    const [currentCouple, setCurrentCouple] = React.useState(null);
    const [openDeleteDialog, setDeleteDialogOpen] = React.useState(false);
    const handleDeleteClose = action => () => {
        if (action == 'agree') {
            deleteCouple(currentCouple.id).then(() => {
                setSnackbarMessage('Successfully deleted couple');
                setOpenSnackbar(true);
                setLeads(leads.filter(x => x.id !== currentCouple.id));
            });
        }
        setDeleteDialogOpen(false);
    };

    const handleDelete = rowData => {
        setCurrentCouple(rowData);
        setDeleteDialogOpen(true);
    }

    const deleteCouple = id => {
        const data = {
            _token: window._token
        }
        return new Promise((resolve, reject) => {
            fetch(`/api/couples/${id}`, {
                method: 'DELETE',
                body: JSON.stringify(data),
                headers: {
                    'Content-Type': 'application/json'
                }
            })
                .then(response => {
                    return response.json();
                })
                .then(result => {
                    setDeleteDialogOpen(false);
                    resolve();
                })
                .catch(reason => {
                    setDeleteDialogOpen(false);
                    alert('An error prevented deleting the couple. Please refresh the page and try again.');
                    reject();
                })
        });
    }

    const fillLeads = () => {
        fetch('/api/couples?weddingStart=2022-01-01&weddingEnd=2026-12-31&is_active=1&couple_type=Lead&orderBy=inquiry_date&orderDirection=desc')
        .then(response => {
            return response.json();
        })
        .then(result => {
            setLeads(result.data);
        });
    }

    const [openSMS, setOpenSMS] = useState(false);
    const handleStartSMS = () => {
        setOpenSMS(true);
    }
    const handleCloseSMS = () => {
        setOpenSMS(!openSMS);
    }

    return (
        <Paper square className={classes.paper}>
            <Typography variant="h6">Import Leads</Typography>
            <Box>
                <Box mt={2}>
                    <Button size="small" variant="contained" onClick={downloadTemplate}>
                        Download Template
                    </Button>
                </Box>
                <Box mt={2}>
                    <Typography variant="subtitle2">1. Upload a CSV</Typography>
                </Box>
                <Box mt={1} display="flex">
                    <Button color="primary" variant="contained" onClick={handleOnClick} startIcon={<CloudUploadIcon />}>
                        Choose File
                    </Button>
                    <Box ml={1}>
                        <Button onClick={handleClear}>Reset</Button>
                    </Box>
                </Box>
                <Box mt={1}>
                    <Typography variant="caption" color="textSecondary">Please use the template above to ensure you have the correct columns</Typography>
                </Box>
                <Box mt={1}>
                    {fileUpload && <Typography variant="subtitle2" color="textSecondary">{fileUpload.name}</Typography>}
                    {fileInfo && <Typography variant="subtitle2" color="textSecondary">{fileInfo} {fileInfo === 1 ? 'row' : 'rows'} in this file</Typography>}
                </Box>
                <input
                    accept=".csv"
                    className={classes.input}
                    id="file"
                    multiple
                    type="file"
                    ref={ref}
                    onChange={handleUpload}
                    onClick={handleFileClick}
                />
                <Box mt={2}>
                    <Typography variant="subtitle2">2. Choose the province</Typography>
                </Box>
                <Box>
                    <TextField
                        select
                        margin="normal"
                        onChange={handleChangeProvince}
                        label="Province"
                        value={province || ''}
                        SelectProps={{
                            autoWidth: true
                        }}
                        variant="outlined"
                        className={classes.province}
                        helperText='This is the province for all couples in the spreadsheet'
                    >
                        {provinces.map(option => (
                            <MenuItem key={option.id} value={option.id}>
                                {option.province_name}
                            </MenuItem>
                        ))}
                    </TextField>
                </Box>
                <Box mt={2} display="flex" gridGap={8}>
                    <Button disabled={loading || !province || !fileUpload} variant="contained" color="primary" onClick={handleSubmit}>Submit</Button>
                    <Button disabled={loading} variant="outlined" onClick={handleBack}>Back</Button>
                    {window.showGetLeads && <Button variant="contained" color="primary" onClick={fillLeads}>Get Leads</Button>}
                </Box>
            </Box>
            <LeadsImportTable leads={leads} handleDelete={handleDelete} handleStartSMS={handleStartSMS} />
            <Snackbar
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                open={openSnackbar}
                autoHideDuration={8000}
                onClose={handleCloseSnackbar}
                message={snackbarMessage}
                action={
                    <React.Fragment>
                        <IconButton size="small" aria-label="close" color="inherit" onClick={handleCloseSnackbar}>
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </React.Fragment>
                }
            />
            <ConfirmDialog
                title='Confirm delete'
                text='Are you sure you wish to delete this couple?'
                agreeText='Delete'
                declineText='Cancel'
                agreeColor='secondary'
                handleClose={handleDeleteClose}
                open={openDeleteDialog}
            />
            <LeadsImportSMS
                leads={leads}
                open={openSMS}
                handleClose={handleCloseSMS}
                setSnackbarMessage={setSnackbarMessage}
                setOpenSnackbar={setOpenSnackbar}
            />
        </Paper>
    )
}

export default LeadsList;
